import type { Ref } from 'vue';
import { watch, ref, nextTick } from 'vue';

export type PersistScope = 'global' | 'route' | 'position';

export interface PersistPositionOptions {
  /**
   * The host element for position-based storage keys
   */
  host?: HTMLElement | null;
  /**
   * Props to identify this component uniquely within its position
   */
  props?: Record<string, any>;
}

export interface PersistOptions<T> {
  key: string;
  scope?: PersistScope;
  /**
   * Options for position-based scoping
   */
  options?: PersistPositionOptions;
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
}

const defaultSerialize = <T>(value: T): string => JSON.stringify(value);
const defaultDeserialize = <T>(value: string): T => JSON.parse(value);

/**
 * Generate a position-based storage key using the element's position in the DOM
 */
function generatePositionKey(
  baseKey: string,
  host: HTMLElement | null | undefined,
  props?: Record<string, any>
): string {
  if (!host) {
    console.warn(
      '[usePersistentRef] No host provided for position scope, falling back to base key'
    );
    return baseKey;
  }

  // Build a path from root to this element
  const path: string[] = [];
  let current: HTMLElement | null = host;

  while (current && current !== document.body) {
    const parent: HTMLElement | null = current.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children);
      const index = siblings.indexOf(current);
      const tagName = current.tagName.toLowerCase();
      path.unshift(`${tagName}[${index}]`);
    }
    current = parent;
  }

  const positionPath = path.join('/');

  // Add props to make the key more specific
  if (props && Object.keys(props).length > 0) {
    const propsHash = Object.entries(props)
      .filter(([value]) => value != null)
      .map(([key, value]) => `${key}:${value}`)
      .join('|');
    return `${baseKey}:${positionPath}:${propsHash}`;
  }

  return `${baseKey}:${positionPath}`;
}

/**
 * Make a ref persistent across page reloads.
 * Returns a NEW ref that is synced with the input ref.
 */
export function usePersistentRef<T>(sourceRef: Ref<T>, options: PersistOptions<T>): Ref<T> {
  const {
    key: baseKey,
    scope = 'global',
    options: positionOptions,
    serialize = defaultSerialize,
    deserialize = defaultDeserialize
  } = options;

  // Determine storage and key based on scope
  let storage: Storage;
  let key: string;

  switch (scope) {
    case 'global':
      storage = localStorage;
      key = baseKey;
      break;
    case 'route':
      storage = sessionStorage;
      key = `${baseKey}:${window.location.pathname}`;
      break;
    case 'position':
      storage = sessionStorage;
      key = generatePositionKey(baseKey, positionOptions?.host, positionOptions?.props);
      break;
    default:
      storage = localStorage;
      key = baseKey;
  }

  // Create a new ref that will be persisted
  const persistedRef = ref(sourceRef.value) as Ref<T>;

  let isHydrating = true;
  let isSaving = false;

  // Hydrate once on initialization
  try {
    const saved = storage.getItem(key);
    if (saved !== null) {
      console.log(`[usePersistentRef] Hydrating ${key} from storage:`, saved);
      persistedRef.value = deserialize(saved);

      // Sync hydrated value back to source ref
      sourceRef.value = persistedRef.value;
    }
  } catch (e) {
    console.warn(`[usePersistentRef] Failed to hydrate ${key}:`, e);
  }

  // Mark hydration complete after a tick
  nextTick(() => {
    isHydrating = false;
  });

  // Watch source ref and update persisted ref + storage
  watch(
    () => sourceRef.value,
    newValue => {
      if (isHydrating) {
        console.log(`[usePersistentRef] Skipping save during hydration for ${key}`);
        return;
      }

      if (isSaving) {
        console.log(`[usePersistentRef] Skipping save - already saving ${key}`);
        return;
      }

      isSaving = true;

      try {
        const serialized = serialize(newValue);
        storage.setItem(key, serialized);
        console.log(`[usePersistentRef] Saving ${key} to storage:`, newValue);
        persistedRef.value = newValue;
      } catch (e) {
        console.warn(`[usePersistentRef] Failed to save ${key}:`, e);
      } finally {
        // Reset flag after a tick to allow other updates to complete
        nextTick(() => {
          isSaving = false;
        });
      }
    },
    { deep: true }
  );

  return persistedRef;
}
